import type { SocketType } from "../../types";
import {
  CUSTOMER_EVIDENCE_KINDS,
  type CustomerEvidenceEntry,
  type CustomerEvidenceKind,
} from "../../steps/accounts-receivable/customers/match-customer-with-evidence.step";
import { configEntryList, prettify } from "./socketDefinition";
import type { DynamicSocketDefinition } from "./socketDefinition";
import {
  resolveKeyedInputSockets,
  type ResolvedKeyedInputSockets,
} from "./keyedInputSockets";

export const MATCH_CUSTOMER_WITH_EVIDENCE_STEP_TYPE =
  "match_customer_with_evidence";

/**
 * The socket type each evidence kind takes. `customer` is untyped because it
 * accepts an AR_CUSTOMER ref, a provenance object or a bare id; invoice
 * numbers are array-typed so a wired list arrives whole instead of fanning
 * the step out once per number.
 */
const SOCKET_TYPE_BY_KIND: Record<CustomerEvidenceKind, SocketType> = {
  customer: "any",
  inbound_email: "entity_reference:INBOUND_EMAIL",
  email_address: "string",
  invoice_numbers: "array<string>",
};

const DESCRIPTION_BY_KIND: Record<CustomerEvidenceKind, string> = {
  customer: "A customer the source knows (customer ref, provenance or id)",
  inbound_email: "An email whose sender is looked up in sender memory",
  email_address: "A sender address looked up in sender memory",
  invoice_numbers: "Invoice numbers matched to the customer that owns them",
};

function isEvidenceKind(value: unknown): value is CustomerEvidenceKind {
  return (CUSTOMER_EVIDENCE_KINDS as readonly unknown[]).includes(value);
}

/**
 * The step's usable evidence entries, in configured order. Read off
 * unvalidated config (the editor and the save path regenerate sockets from
 * client-sent config), so malformed entries are dropped, not thrown on.
 */
export function matchCustomerEvidenceEntries(
  config: Record<string, unknown>,
): CustomerEvidenceEntry[] {
  return configEntryList<{ key?: unknown; label?: unknown; kind?: unknown }>(
    config.evidence,
  ).flatMap((entry) =>
    typeof entry.key === "string" &&
    entry.key.length > 0 &&
    isEvidenceKind(entry.kind)
      ? [
          {
            key: entry.key,
            kind: entry.kind,
            ...(typeof entry.label === "string" && entry.label.length > 0
              ? { label: entry.label }
              : {}),
          },
        ]
      : [],
  );
}

/** One input socket per configured evidence entry, and each key's socket id. */
export function resolveMatchCustomerWithEvidenceInputSockets(
  config: Record<string, unknown>,
): ResolvedKeyedInputSockets {
  return resolveKeyedInputSockets(
    MATCH_CUSTOMER_WITH_EVIDENCE_STEP_TYPE,
    config,
    matchCustomerEvidenceEntries(config).map((entry) => ({
      key: entry.key,
      socket: {
        name: entry.label ?? prettify(entry.key),
        type: SOCKET_TYPE_BY_KIND[entry.kind],
        required: false,
        description: DESCRIPTION_BY_KIND[entry.kind],
      },
    })),
  );
}

export function generateMatchCustomerWithEvidenceInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveMatchCustomerWithEvidenceInputSockets(config).sockets;
}
